import { WritableDraft } from 'immer';
import { InvalidStateError } from '@/common/errors';
import { Appliance, Diffuser, DraggableDevice, Group, Hood, VentilatedCeiling } from '@/types/helpers';
import { ApplianceId, GroupId, HoodId, RowId } from '@/api/airchef/Api';
import { AssetRemoveGroupInfo } from '@/types/stores/project';
import { BuildSlice } from '@/types/stores/build';
import { PlanSlice } from '@/types/stores/plan';
import { PlannerSlice } from '@/types/stores/planner';
import { ProjectSlice } from '@/types/stores/project';
import { RootActions } from '@/types/stores/rootActions';

type StoreState = BuildSlice & PlanSlice & PlannerSlice & ProjectSlice & RootActions;

/**
 * Find an asset (hood or appliance) by its ID
 */
export function findAssetToRemove(
  id: string,
  project: {
    appliances: Record<string, Appliance>;
    hoods: Record<string, Hood>;
    diffusers: Record<string, Diffuser>;
    ceilings: Record<string, VentilatedCeiling>;
  },
): DraggableDevice | null {
  return (project.appliances[id] ??
    project.hoods[id] ??
    project.diffusers[id] ??
    project.ceilings[id] ??
    null) as DraggableDevice | null;
}

/**
 * Collect all hoods that need to be removed when removing a hood from a group
 */
export function collectHoodGroupDeletions(hood: Hood, groups: Record<string, Group>): HoodId[] {
  const assetsToRemove: HoodId[] = [hood.id];

  if (hood.relations.groupId) {
    const hoodGroup = groups[hood.relations.groupId];
    if (hoodGroup) {
      assetsToRemove.push(...hoodGroup.relations.hoodIds);
    }
  }

  return assetsToRemove;
}

/**
 * Collect all groups, rows, and related information that need to be removed
 * when removing an appliance from a group
 */
export function collectApplianceGroupDeletions(
  appliance: Appliance,
  groups: Record<string, Group>,
  appliances: Record<string, Appliance>,
): {
  groupsToRemove: GroupId[];
  rowsToRemove: RowId[];
  spacersToRemove: ApplianceId[];
  rowNum: number;
  firstRowId?: RowId;
  groupInfo?: AssetRemoveGroupInfo;
  wasFloating?: boolean;
} {
  const result = {
    groupsToRemove: [] as GroupId[],
    rowsToRemove: [] as RowId[],
    spacersToRemove: [] as ApplianceId[],
    rowNum: -1,
    firstRowId: undefined as RowId | undefined,
    groupInfo: undefined as AssetRemoveGroupInfo | undefined,
  };

  if (!appliance.relations.groupId) return result;

  const applianceGroup = groups[appliance.relations.groupId];
  if (!applianceGroup) return result;

  // Validate row number
  result.rowNum = appliance.relations.rowNum ?? -1;
  if (result.rowNum < 0) {
    throw new InvalidStateError(
      `No row number found for grouped asset ${appliance.haltonValues.type} [${appliance.id}]`,
    );
  }
  if (result.rowNum > 1) {
    throw new InvalidStateError(
      `Unsupported row number (${result.rowNum}) found for grouped asset ${appliance.haltonValues.type} [${appliance.id}]`,
    );
  }

  const row = applianceGroup.rows[result.rowNum];

  const isFloating = row.relations.floatingApplianceIds.some((ap) => ap.applianceId === appliance.id);

  if (!isFloating) {
    // Check if group should be removed (last appliance)
    const groupAppliances = applianceGroup.relations.applianceIds
      .map((aid) => appliances[aid])
      .filter(Boolean)
      .filter((a) => a.haltonValues.type !== 'spacer')
      .map((a) => a.id);

    if (groupAppliances.length <= 1) {
      result.groupsToRemove.push(applianceGroup.id);
      // Collect all spacers in the group for removal
      const groupSpacers = applianceGroup.relations.applianceIds
        .map((aid) => appliances[aid])
        .filter(Boolean)
        .filter((a) => a.haltonValues.type === 'spacer')
        .map((a) => a.id);
      result.spacersToRemove.push(...groupSpacers);
    }
  }

  result.firstRowId = row?.id;

  // Check if row should be removed
  if (row && !result.groupsToRemove.includes(applianceGroup.id)) {
    const rowAppliances = row.relations.applianceIds
      .map((aid) => appliances[aid])
      .filter(Boolean)
      .filter((a) => a.haltonValues.type !== 'spacer')
      .map((a) => a.id);

    if (rowAppliances.includes(appliance.id) && rowAppliances.length <= 1) {
      result.rowsToRemove.push(row.id);
      // Collect all spacers in the row for removal
      const rowSpacers = row.relations.applianceIds
        .map((aid) => appliances[aid])
        .filter(Boolean)
        .filter((a) => a.haltonValues.type === 'spacer')
        .map((a) => a.id);
      result.spacersToRemove.push(...rowSpacers);
    }
  }

  // Build group info for return
  const rowIndex = row?.relations.applianceIds.indexOf(appliance.id) ?? -1;
  if (rowIndex >= 0) {
    result.groupInfo = {
      index: rowIndex,
      rowNum: result.rowNum as 0 | 1,
      wasFirst: rowIndex === 0,
      wasLast: rowIndex === applianceGroup.relations.applianceIds.length - 1,
      groupId: applianceGroup.id,
    };
  }

  return {
    ...result,
    wasFloating: isFloating,
  };
}

/**
 * Remove hood references from its group
 */
export function removeHoodFromGroup(state: WritableDraft<StoreState>, hoodIds: HoodId[], groupId: GroupId): void {
  const hoodGroup = state.project.data.groups[groupId];
  if (!hoodGroup) return;

  hoodGroup.relations.hoodIds = hoodGroup.relations.hoodIds.filter((hid) => !hoodIds.includes(hid));

  for (const row of hoodGroup.rows) {
    if (!row) continue;
    row.relations.hoodIds = row.relations.hoodIds.filter((hid) => !hoodIds.includes(hid));
  }

  for (const aid of hoodGroup.relations.applianceIds) {
    const appliance = state.project.data.appliances[aid];
    if (!appliance) {
      hoodGroup.relations.applianceIds.filter((naid) => naid !== aid);
      continue;
    }

    appliance.relations.hoodIds = appliance.relations.hoodIds?.filter((hid) => !hoodIds.includes(hid));
    // @ts-expect-error hoodIds above is not typed, and possibly removed
    appliance.relations.hoodIdsAbove = appliance.relations.hoodIdsAbove?.filter((hid) => !hoodIds.includes(hid));
  }
}

/**
 * Remove appliance references from its group
 */
export function removeApplianceFromGroup(state: WritableDraft<StoreState>, appliance: Appliance, rowNum: number): void {
  if (!appliance.relations.groupId) return;

  const group = state.project.data.groups[appliance.relations.groupId];
  if (!group) return;

  group.relations.applianceIds = group.relations.applianceIds.filter((aid) => aid !== appliance.id);
  group.rows[rowNum].relations.floatingApplianceIds = group.rows[rowNum].relations.floatingApplianceIds.filter(
    (ap) => ap.applianceId !== appliance.id,
  );
  group.rows[rowNum].relations.applianceIds = group.rows[rowNum].relations.applianceIds.filter(
    (aid) => aid !== appliance.id,
  );
}

/**
 * Remove groups and their associated hoods from the project
 */
export function removeGroups(state: WritableDraft<StoreState>, groupIds: GroupId[]): void {
  groupIds.forEach((gid) => {
    const group = state.project.data.groups[gid];

    // Remove hoods in the group
    group.relations.hoodIds.forEach((hid) => {
      delete state.project.data.hoods[hid];
    });

    // Remove the group itself
    delete state.project.data.groups[gid];
  });
}

/**
 * Remove rows from a group and reindex remaining appliances
 */
export function removeRowsAndReindex(
  state: WritableDraft<StoreState>,
  groupId: GroupId,
  rowIdsToRemove: RowId[],
): void {
  const group = state.project.data.groups[groupId];
  if (!group) return;

  // Remove the rows
  // @ts-expect-error typed to specific size
  group.rows = group.rows.filter((r) => !rowIdsToRemove.includes(r.id));

  // Reindex remaining appliances
  group.rows.forEach((row, rowNum) => {
    row.relations.applianceIds.forEach((aid) => {
      state.project.data.appliances[aid].relations.rowNum = rowNum;
      state.project.data.appliances[aid].relations.rowId = row.id;
    });
  });
}

/**
 * Update group transform when the first row changes
 */
export function updateGroupTransformIfNeeded(
  state: WritableDraft<StoreState>,
  groupId: GroupId,
  previousFirstRowId: RowId | undefined,
): void {
  const group = state.project.data.groups[groupId];
  if (!group?.rows[0]) return;

  if (previousFirstRowId !== undefined && previousFirstRowId !== group.rows[0].id) {
    const firstAppliance = state.project.data.appliances[group.rows[0].relations.applianceIds[0]];
    if (firstAppliance) {
      group.airchefValues.transform.position =
        firstAppliance.airchefValues.offsetTransform?.position ?? firstAppliance.airchefValues.transform.position;
      group.airchefValues.transform.rotation = firstAppliance.airchefValues.transform.rotation;
    }
  }
}
